import {
  EHeadingStyleVariant,
  ELinkVariant,
  ETextStyleVariant,
  Heading,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import lineOfbusinessHero from '@/assets/about/outdoorsy-group/line-of-business-hero.jpg';
import lineOfbusiness1 from '@/assets/about/outdoorsy-group/line-of-business1.jpg';
import lineOfbusiness2 from '@/assets/about/outdoorsy-group/line-of-business2.jpg';
import lineOfbusiness3 from '@/assets/about/outdoorsy-group/line-of-business3.jpg';
import OdnLogo from '@/assets/about/outdoorsy-group/odn-logo.png';
import OutdoorsyLogo from '@/assets/about/outdoorsy-group/outdoorsy-logo.svg';
import RoamlyLogo from '@/assets/about/outdoorsy-group/roamly-logo.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const LineOfBusiness = () => {
  const intl = useIntl();

  const { isMobile, isAboveTablet, isAboveDesktop, isAboveXXXL } = useBreakpoint();

  let businessItemHeaderVariant = EHeadingStyleVariant.H4;

  if (isAboveXXXL) {
    businessItemHeaderVariant = EHeadingStyleVariant.H3;
  }

  return (
    <section>
      <div className="py-20 md:py-24 xxxl:py-48 bg-[#D2C5BC]">
        <MaxWidth className="px-6 mx-auto">
          <div className="mx-auto w-[250px] xs:w-[342px] md:w-[624px] xl:w-[900px]">
            <Image
              className="bg-[#D2C5BC]"
              src={lineOfbusinessHero}
              alt="Outdoorsy Group lines of business"
              role="presentation"
              width={0}
              height={0}
              style={{ width: '100%', height: '60%' }}
              layout="responsive"
              objectFit="contain"
              objectPosition="center"
              loading="lazy"
            />
          </div>

          <Heading
            className="mt-10 text-white md:mt-20 md:text-center"
            variant={isAboveTablet ? EHeadingStyleVariant.H2Alt : EHeadingStyleVariant.H3Alt}>
            <FormattedMessage
              defaultMessage="The Outdoorsy Group is a travel and hospitality company that integrates marketplace technology, insurance, and real estate to create unforgettable outdoor experiences."
              id="ggPlZX"
            />
          </Heading>
        </MaxWidth>
      </div>

      <div className="py-20 bg-white md:py-24 xxxl:py-48 xxl:mx-auto xxl:max-w-xxl">
        <div className="flex flex-col items-center px-6">
          <Heading
            className="pb-12 xl:pb-36"
            variant={isMobile ? EHeadingStyleVariant.H2Alt : EHeadingStyleVariant.H3Alt}>
            <FormattedMessage defaultMessage="Our lines of business" id="l1GKTH" />
          </Heading>

          <div className="flex flex-col items-center pb-20 md:flex-row gap-x-14 gap-y-6  xl:pb-36">
            <div className="w-full">
              <Image
                src={lineOfbusiness1}
                alt="outdoors image"
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '60%' }}
                layout="responsive"
                objectFit="contain"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col">
              <OutdoorsyLogo />
              <Heading className="py-6" variant={businessItemHeaderVariant}>
                <FormattedMessage
                  defaultMessage="The world's leading outdoor travel marketplace"
                  id="80pC2Y"
                />
              </Heading>
              <Text
                variant={
                  isAboveDesktop ? ETextStyleVariant.LargeRegular : ETextStyleVariant.MediumRegular
                }>
                <FormattedMessage
                  defaultMessage="Our marketplace connects guests with P2P RV rentals and professionalized businesses offering RVs and campgrounds. We operate globally in 14 countries, with North America as our strategic focus."
                  id="6xFdv9"
                />
              </Text>
              <Link
                href="/"
                label={intl.formatMessage({
                  defaultMessage: 'Visit Outdoorsy.com',
                  id: 'n92Brn',
                })}
                variant={ELinkVariant.Underline}
                className="pt-6"
                target="_blank"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse items-center pb-20 md:flex-row gap-x-14 gap-y-6  xl:pb-36">
            <div className="flex flex-col">
              <RoamlyLogo />
              <Heading className="py-6" variant={businessItemHeaderVariant}>
                <FormattedMessage defaultMessage="Insurance that moves marketplaces" id="eb1IAZ" />
              </Heading>
              <Text
                variant={
                  isAboveDesktop ? ETextStyleVariant.LargeRegular : ETextStyleVariant.MediumRegular
                }>
                <FormattedMessage
                  defaultMessage="Our insurance company provides customized insurance solutions for RV owners and guests, ensuring peace of mind for any outdoor adventure. Roamly's tech-driven platform also supports other marketplaces."
                  id="ikTvfD"
                />
              </Text>
              <Link
                href="https://roamly.com/"
                label={intl.formatMessage({
                  defaultMessage: 'Visit Roamly.com',
                  id: 'tkTF+m',
                })}
                variant={ELinkVariant.Underline}
                className="pt-6"
                target="_blank"
              />
            </div>

            <div className="w-full">
              <Image
                src={lineOfbusiness2}
                alt="roamly image"
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '60%' }}
                layout="responsive"
                objectFit="contain"
                loading="lazy"
              />
            </div>
          </div>

          <div className="flex flex-col items-center md:flex-row gap-x-14 gap-y-6 ">
            <div className="w-full">
              <Image
                src={lineOfbusiness3}
                alt="outdoorsy destinations image"
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '60%' }}
                layout="responsive"
                objectFit="contain"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col">
              <Image src={OdnLogo} alt="odn logo" role="presentation" width={200} height={45} />
              <Heading className="py-6" variant={businessItemHeaderVariant}>
                <FormattedMessage
                  defaultMessage="Stay in the world's most epic outdoor locations"
                  id="ykMhSc"
                />
              </Heading>
              <Text
                variant={
                  isAboveDesktop ? ETextStyleVariant.LargeRegular : ETextStyleVariant.MediumRegular
                }>
                <FormattedMessage
                  defaultMessage="Established in 2022, this line of business focuses on owning and operating campgrounds and glamping sites in iconic locations, providing exclusive experiences and seamless integration with Outdoorsy.com."
                  id="EzTzDe"
                />
              </Text>
              <Text className="pt-6">
                <FormattedMessage
                  defaultMessage="Explore the Outdoorsy Destination Network: {hillCountryLink}, {yosemiteLink}, and {bayfieldLink}."
                  id="q9RRgU"
                  values={{
                    hillCountryLink: (
                      <Link
                        href="/destinations/outdoorsy-hill-country"
                        label="Hill Country"
                        variant={ELinkVariant.Underline}
                        target="_blank"
                      />
                    ),
                    yosemiteLink: (
                      <Link
                        href="/destinations/outdoorsy-yosemite"
                        label="Yosemite"
                        variant={ELinkVariant.Underline}
                        target="_blank"
                      />
                    ),
                    bayfieldLink: (
                      <Link
                        href="/destinations/outdoorsy-bayfield"
                        label="Bayfield"
                        variant={ELinkVariant.Underline}
                        target="_blank"
                      />
                    ),
                  }}
                />
              </Text>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LineOfBusiness;
